import { FORWARDER_ADDRESS, BEACON_FACTORY_ADDRESS } from './constants';
import { BEACON_FACTORY_ABI } from '@/data/beacon-factory-abi';
import { ConnectedWallet } from '@privy-io/react-auth';
import {
  GetIdentityDetailResponse,
  BeaconFactoryEvent,
  BeaconFactoryMethod,
  IdentityModuleMethod,
  TWalletClient,
} from './types';
import { publicClient } from '../contract-utils';
import { encodeFunctionData, Hex, parseEventLogs, toBytes, toHex } from 'viem';
import {
  generateRandomRSAKeyPair,
  exportKeyPair,
  generateRandomNonce,
} from '../crypto-utils';

import { decryptAES, encryptAES } from './aes-utils';
import {
  buildAgentKey,
  buildMasterKey,
  buildSigningKey,
  getAg2Hash,
  getNewSignedMessage,
} from './key-derivation-utils';

import { fromUint8Array, toUint8Array } from 'js-base64';
import { AgentInstance } from './smart-account-agent';
import { IDENTITY_MODULE_ABI } from '@/data/identity-module-abi';
import {
  addIdentityModuleCache,
  getIdentityModuleCache,
  modifyUserIdentity,
} from '@/db/queries';
import { IdentityModuleCache } from '@/db/db';
import { DEFAULT_TIMEOUT } from '../account-utils';

export const getWalletIdentityModuleAddress = async (
  wallet: ConnectedWallet
): Promise<Hex> => {
  const response = await publicClient.readContract({
    address: BEACON_FACTORY_ADDRESS,
    abi: BEACON_FACTORY_ABI,
    functionName: BeaconFactoryMethod.IdentityModuleAddress,
    args: [wallet.address],
  });
  return response as Hex;
};

export const createNewIdentity = async (walletClient: TWalletClient) => {
  const signedMessage = await getNewSignedMessage(walletClient);
  const salt = generateRandomNonce();

  const ag2Hash = await getAg2Hash(signedMessage, salt);
  const masterKey = await buildMasterKey(ag2Hash, salt);

  const signingKey = buildSigningKey(ag2Hash, salt);

  const agentKey = buildAgentKey(ag2Hash, salt);

  await AgentInstance.initializeAgentClient(agentKey);

  const rsaKeyPair = await generateRandomRSAKeyPair();
  const { publicKey: rsaPublicKey, privateKey: rsaPrivateKey } =
    await exportKeyPair(rsaKeyPair);

  const publicKeyBytes = new Uint8Array(rsaPublicKey);
  const privateKeyBytes = new Uint8Array(rsaPrivateKey);

  const encPrivateKeyBytes = await encryptAES(privateKeyBytes, masterKey);

  const encodedCallData = encodeFunctionData({
    abi: BEACON_FACTORY_ABI,
    functionName: BeaconFactoryMethod.CreateNewIdentityModule,
    args: [
      walletClient.account.address,
      FORWARDER_ADDRESS,
      [
        toHex(salt),
        signingKey.did(),
        toHex(publicKeyBytes),
        toHex(toUint8Array(encPrivateKeyBytes)),
        AgentInstance.getAgentAddress(),
      ],
    ],
  });

  const trxRecipt = await AgentInstance.executeUserOperationRequest(
    {
      contractAddress: BEACON_FACTORY_ADDRESS,
      data: encodedCallData,
    },
    DEFAULT_TIMEOUT
  );

  if (!trxRecipt.success) throw new Error('Failed to create identity');

  const parsedLogs = parseEventLogs({
    abi: BEACON_FACTORY_ABI,
    eventName: BeaconFactoryEvent.NewIdentityModule,
    logs: trxRecipt.logs,
  });

  if (parsedLogs.length === 0) throw new Error('Failed to create identity');
  // @ts-ignore
  const identityModuleAddress = parsedLogs[0].args.identityModule;

  const confirmations = await publicClient.waitForTransactionReceipt({
    hash: trxRecipt.receipt.transactionHash,
    confirmations: 2,
    timeout: 30000,
  });

  if (confirmations.status !== 'success')
    throw new Error('Failed to get transaction confirmations');

  await modifyUserIdentity(walletClient.account.address as Hex, {
    identityModuleAddress,
  });

  const identityModule: IdentityModuleCache = {
    moduleAddress: identityModuleAddress,
    salt: salt,
    ag2Hash,
    accountPublicKey: toHex(publicKeyBytes),
    accountPrivateKey: toHex(toUint8Array(encPrivateKeyBytes)),
    signingDid: signingKey.did(),
    agentAddress: AgentInstance.getAgentAddress(),
  };

  await addIdentityModuleCache(identityModule);

  return {
    identityModuleAddress,
    signingKey,
    publicKey: publicKeyBytes,
    privateKey: privateKeyBytes,
    salt,
  };
};

export const getIdentityModuleDetails = async (
  walletClient: TWalletClient,
  identityModuleAddress: Hex
): Promise<IdentityModuleCache> => {
  const cachedDetails = await getIdentityModuleCache(identityModuleAddress);
  if (cachedDetails) return cachedDetails;

  const response = (await publicClient.readContract({
    address: identityModuleAddress,
    abi: IDENTITY_MODULE_ABI,
    functionName: IdentityModuleMethod.GetIdentityModuleDetail,
    account: walletClient.account,
  })) as GetIdentityDetailResponse;

  const {
    accountPrivateKey,
    accountPublicKey,
    agentAddress,
    salt,
    signingDid,
  } = response;

  const signedMessage = await getNewSignedMessage(walletClient);
  const saltBytes = toBytes(toHex(salt));
  const ag2Hash = await getAg2Hash(signedMessage, saltBytes);

  const identityModule: IdentityModuleCache = {
    moduleAddress: identityModuleAddress,
    accountPublicKey,
    accountPrivateKey,
    salt: saltBytes,
    ag2Hash,
    signingDid,
    agentAddress,
  };

  await addIdentityModuleCache(identityModule);

  return identityModule;
};

export const getIdentityModule = async (
  walletClient: TWalletClient,
  identityModuleAddress: Hex
) => {
  const identityModule = await getIdentityModuleDetails(
    walletClient,
    identityModuleAddress
  );

  const {
    accountPrivateKey,
    accountPublicKey,
    agentAddress,
    salt: saltBytes,
    ag2Hash,
    signingDid,
  } = identityModule;

  const masterKey = await buildMasterKey(ag2Hash, saltBytes);
  const signingKey = buildSigningKey(ag2Hash, saltBytes);
  const agentKey = buildAgentKey(ag2Hash, saltBytes);

  await AgentInstance.initializeAgentClient(agentKey);

  if (signingDid !== signingKey.did())
    throw new Error('Signin DID does not match signing key');

  const decryptedPrivateKey = await decryptAES(
    fromUint8Array(toBytes(accountPrivateKey)),
    masterKey
  );

  return {
    privateKey: decryptedPrivateKey,
    publicKey: toBytes(accountPublicKey),
    agentAddress,
    signingKey,
    salt: saltBytes,
  };
};
